// Pure reducer for the Battle screen. The v2 wire format ships events
// (not snapshots): the initial `MatchSnapshot` seeds `entities` / `defs`
// / scalars / `pending`; each `WireEvent` thereafter patches one slice
// of that state.
//
// Multiple writers (HTTP action response, channel push, concede,
// opponent-ended, engine-finalized) funnel through here so the
// terminal-state lock — set when the match ends — drops any racing
// engine events that would otherwise clobber the banner.
//
// Gap detection lives in BattlePage (it checks `event.seq ===
// lastEventSeq + 1`); the reducer just applies in order.

import type {
  AttrPatch,
  CombatSnapshot,
  EntityDef,
  EntityRecord,
  GameResult,
  MatchSnapshot,
  Pending,
  Phase,
  PlayerId,
  PlayerScalars,
  WireEvent,
} from "../api/types";
import type { FeedEntry } from "./feed";

export type { FeedEntry };

export interface BattleState {
  /** Null until `GET /state` lands. */
  viewer: PlayerId | null;
  turn: number;
  phase: Phase;
  activePlayer: PlayerId;
  players: [PlayerScalars, PlayerScalars];
  entities: Map<number, EntityRecord>;
  defs: Map<string, EntityDef>;
  combat: CombatSnapshot | null;
  pending: Pending | null;
  result: GameResult | null;
  /** Last applied event seq. `-1` means no event has landed yet. */
  lastEventSeq: number;
  /** Match locally terminal — concede / opponent-ended / engine result. */
  terminal: boolean;
  /** Banner feed appended to as events land. */
  eventLog: FeedEntry[];
}

function defaultScalars(seat: PlayerId): PlayerScalars {
  return {
    player_id: seat,
    life: 0,
    mana: 0,
    max_mana: 0,
    hand_size: 0,
    deck_size: 0,
    graveyard_size: 0,
    exile_size: 0,
    hand: [],
    deck: [],
    battle: [],
    graveyard: [],
    exile: [],
  };
}

export const initialBattleState: BattleState = {
  viewer: null,
  turn: 0,
  phase: "main",
  activePlayer: 0,
  players: [defaultScalars(0), defaultScalars(1)],
  entities: new Map(),
  defs: new Map(),
  combat: null,
  pending: null,
  result: null,
  lastEventSeq: -1,
  terminal: false,
  eventLog: [],
};

export type BattleAction =
  /** Cold load or hard refetch (`GET /state`). Resets entities/defs
   *  to the server view; preserves the event log across reconnects. */
  | { type: "loaded"; snapshot: MatchSnapshot }
  /** Rehydrate the event log from a side store (localStorage) at
   *  page-refresh time. Replaces the current log wholesale — only
   *  used at mount before any events have landed, so there's nothing
   *  to merge against. */
  | { type: "hydrate_event_log"; entries: FeedEntry[] }
  /** Append a client-side banner entry to the event log. */
  | { type: "log_banner"; entry: FeedEntry }
  /** Apply one or more wire events from the HTTP response or a channel
   *  push. The caller verified seq continuity; this reducer trusts it. */
  | { type: "apply_events"; events: WireEvent[] }
  /** Idempotent — may dispatch twice (pre-DELETE lock, post-DELETE
   *  no-op now that progression is always nil). */
  | { type: "conceded"; loserSeat: PlayerId }
  /** Server pushes a per-viewer outcome on opponent's concede / claim. */
  | { type: "opponent_ended"; viewer: PlayerId; outcome: "win" | "loss" }
  /** Engine declared a result itself; nothing left to stamp here. */
  | { type: "engine_finalized" }
  /** Spectator-only: terminal result arrived via the `game_ended`
   *  channel push (server included it so spectators can synthesize the
   *  banner without re-fetching the deleted engine session). */
  | { type: "set_terminal_result"; result: GameResult };

export function reduceBattle(
  state: BattleState,
  action: BattleAction,
): BattleState {
  switch (action.type) {
    case "loaded": {
      const snap = action.snapshot;
      const entities = new Map<number, EntityRecord>();
      for (const [k, v] of Object.entries(snap.entities)) {
        // Wire encodes the key as a stringified eid; preserve types.
        entities.set(Number(k), v);
      }
      const defs = new Map<string, EntityDef>();
      for (const [k, v] of Object.entries(snap.defs)) defs.set(k, v);
      // Seed the event log with a Turn banner on cold load so the user
      // sees "Your Turn N" before the first per-action event arrives.
      // The wire's per-action Turn event only fires on a transition
      // (turn number or active player change), and the initial snapshot
      // is the only place that pre-state lives, so this banner has to
      // come from the snapshot itself.
      const eventLog =
        state.eventLog.length === 0
          ? [
              {
                kind: "turn_banner" as const,
                parts: [
                  {
                    type: "text" as const,
                    text:
                      snap.active_player === snap.viewer
                        ? `── Your Turn ${snap.turn} ──`
                        : `── Opp's Turn ${snap.turn} ──`,
                  },
                ],
              },
            ]
          : state.eventLog;
      return {
        ...state,
        viewer: snap.viewer,
        turn: snap.turn,
        phase: snap.phase,
        activePlayer: snap.active_player,
        players: snap.players,
        entities,
        defs,
        // Normalize: the v2 wire uses `skip_serializing_if = Option::is_none`
        // for combat / pending / result, so when the engine has no
        // value JSON omits the field entirely and JS surfaces it as
        // `undefined`. Force-coerce to `null` here so downstream
        // strict-`!== null` consumers (action context, pending picker,
        // game-over banner) work for fresh matches too.
        combat: snap.combat ?? null,
        pending: snap.pending ?? null,
        result: snap.result ?? null,
        lastEventSeq: snap.last_event_seq,
        terminal: state.terminal || snap.result != null,
        eventLog,
      };
    }

    case "hydrate_event_log": {
      return { ...state, eventLog: action.entries };
    }

    case "log_banner": {
      return { ...state, eventLog: [...state.eventLog, action.entry] };
    }

    case "apply_events": {
      if (action.events.length === 0) return state;
      // Terminal lock — the single check that protects a synthesized
      // concede/forfeit result from being clobbered by queued events.
      if (state.terminal) return state;
      let next = state;
      for (const ev of action.events) {
        if (ev.seq <= next.lastEventSeq) continue;
        next = applyEvent(next, ev);
        next = { ...next, lastEventSeq: ev.seq };
        if (next.terminal) break;
      }
      return next;
    }

    case "conceded": {
      if (state.viewer == null) return state;
      // Never overwrite an engine-declared result.
      const winnerSeat = (1 - action.loserSeat) as PlayerId;
      const result =
        state.result ?? { kind: "winner" as const, player: winnerSeat };
      return { ...state, result, terminal: true };
    }

    case "opponent_ended": {
      if (state.viewer == null) return state;
      const winnerSeat: PlayerId =
        action.outcome === "win"
          ? action.viewer
          : ((1 - action.viewer) as PlayerId);
      const result =
        state.result ?? { kind: "winner" as const, player: winnerSeat };
      return { ...state, result, terminal: true };
    }

    case "engine_finalized": {
      // Terminal flag + result are already set by the result-bearing
      // event that triggered the DELETE; nothing to do.
      return state;
    }

    case "set_terminal_result": {
      if (state.viewer == null) return state;
      const result = state.result ?? action.result;
      return { ...state, result, terminal: true };
    }
  }
}

// Apply a single wire event. Mutates a shallow-cloned `state` in place
// for the event-specific slice; everything else passes through. Returns
// a new state. `terminal` is lifted by `apply_events` after each step.
function applyEvent(state: BattleState, ev: WireEvent): BattleState {
  switch (ev.k) {
    case "reveal": {
      const entities = new Map(state.entities);
      entities.set(ev.eid, ev.record);
      let defs = state.defs;
      if (ev.def && !defs.has(ev.def.def_id)) {
        defs = new Map(defs);
        defs.set(ev.def.def_id, ev.def);
      }
      // Ad-hoc troops (Lua's `troop(...)` helper) materialise
      // directly into a zone — no preceding `move` event fires for
      // them — so the per-seat ordered zone arrays in `PlayerScalars`
      // never see the new eid through the move path. Without this
      // append, the troop is in `entities` but absent from
      // `players[seat].battle`, so `selectGameView` skips it and the
      // card never renders. Append here so summons show up.
      let players = state.players;
      const seat = ev.record.controller;
      if (seat != null) {
        const cur = state.players[seat];
        const updatedZone = appendRevealToZone(cur, ev.record.zone, ev.eid);
        if (updatedZone !== cur) {
          players = [state.players[0], state.players[1]] as [
            PlayerScalars,
            PlayerScalars,
          ];
          players[seat] = updatedZone;
        }
      }
      return { ...state, entities, defs, players };
    }
    case "move": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (cur) {
        // Engine sets `entered_this_turn = true` whenever an entity
        // enters the battlefield; `summoning_sickness` on the wire is
        // derived from that flag and the engine NEVER re-emits it
        // through an `attr` event when it changes. Without this client-
        // side mirror, a non-Rush creature played this turn keeps
        // `summoning_sickness: false` from when it was first revealed
        // in hand, `computeCanAttack` lets the player declare it, and
        // the engine then silently drops the eid from the attacker set
        // — the player sees only their other attackers go.
        const enteringBattle = ev.to === "battle" && cur.zone !== "battle";
        const hasRush = cur.keywords.some((k) => k.name === "rush");
        entities.set(ev.eid, {
          ...cur,
          zone: ev.to,
          ...(enteringBattle && !hasRush ? { summoning_sickness: true } : {}),
        });
      }
      // Mirror the engine's zone Vecs: drop the eid from the seat's
      // `from` list, append to its `to` list. Stays in lock-step with
      // `Game::submit_main_play`'s `hand_idx` arithmetic. Only run for
      // a known seat — face-down opp moves we can't see won't reach
      // here in the first place, but be defensive.
      const seat = cur?.controller;
      if (seat == null) return { ...state, entities };
      // Use the entity's CURRENT zone (what the SPA had) as the source
      // we remove from, NOT `ev.from`. The engine emits `from: "stack"`
      // for casts even though hand→stack happens silently from the
      // SPA's perspective; trusting `ev.from` would leave the card
      // stranded in hand and shove hand_idx out of alignment.
      const fromZone = cur?.zone ?? ev.from ?? "";
      const cur_scalars = state.players[seat];
      const next: PlayerScalars = {
        ...cur_scalars,
        hand: removeFromZone(cur_scalars.hand, fromZone, "hand", ev.eid),
        deck: removeFromZone(cur_scalars.deck, fromZone, "deck", ev.eid),
        battle: removeFromZone(cur_scalars.battle, fromZone, "battle", ev.eid),
        graveyard: removeFromZone(
          cur_scalars.graveyard,
          fromZone,
          "graveyard",
          ev.eid,
        ),
        exile: removeFromZone(cur_scalars.exile, fromZone, "exile", ev.eid),
      };
      next.hand = appendToZone(next.hand, ev.to, "hand", ev.eid);
      next.deck = appendToZone(next.deck, ev.to, "deck", ev.eid);
      next.battle = appendToZone(next.battle, ev.to, "battle", ev.eid);
      next.graveyard = appendToZone(
        next.graveyard,
        ev.to,
        "graveyard",
        ev.eid,
      );
      next.exile = appendToZone(next.exile, ev.to, "exile", ev.eid);
      const players: [PlayerScalars, PlayerScalars] = [
        state.players[0],
        state.players[1],
      ];
      players[seat] = next;
      return { ...state, entities, players };
    }
    case "attr": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (!cur) return { ...state, entities };
      entities.set(ev.eid, applyAttrPatch(cur, ev.fields));
      // A control change keeps the card in the battle zone but flips
      // which seat owns it. The per-seat `battle` arrays drive board
      // rendering, so move the eid from the old controller's list to the
      // new one's — patching the record alone leaves the card visually
      // stuck on its former owner's side.
      const to = ev.fields.controller;
      if (to != null && to !== cur.controller && cur.zone === "battle") {
        const players: [PlayerScalars, PlayerScalars] = [
          state.players[0],
          state.players[1],
        ];
        players[cur.controller] = {
          ...players[cur.controller],
          battle: players[cur.controller].battle.filter((e) => e !== ev.eid),
        };
        players[to] = {
          ...players[to],
          battle: players[to].battle.includes(ev.eid)
            ? players[to].battle
            : [...players[to].battle, ev.eid],
        };
        return { ...state, entities, players };
      }
      return { ...state, entities };
    }
    case "keyword_add": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (cur) {
        const keywords = cur.keywords.filter((k) => k.name !== ev.name);
        keywords.push({ name: ev.name as never, x: ev.x });
        // Static Rush is granted by the card's `on_play` script
        // AFTER the engine emits the hand→battle Move. The Move
        // handler defensively sets summoning_sickness=true (since
        // the keywords array is still empty at that moment); clear
        // it back to false now that Rush has landed, mirroring the
        // engine's `entered_this_turn && !rush` check.
        const clearSickness =
          ev.name === "rush" && cur.summoning_sickness;
        entities.set(ev.eid, {
          ...cur,
          keywords,
          ...(clearSickness ? { summoning_sickness: false } : {}),
        });
      }
      return { ...state, entities };
    }
    case "keyword_remove": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (cur) {
        entities.set(ev.eid, {
          ...cur,
          keywords: cur.keywords.filter((k) => k.name !== ev.name),
        });
      }
      return { ...state, entities };
    }
    case "counter": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (cur) {
        const named_counters = cur.named_counters.filter(
          (c) => c.name !== ev.name,
        );
        if (ev.count > 0) named_counters.push({ name: ev.name, count: ev.count });
        entities.set(ev.eid, { ...cur, named_counters });
      }
      return { ...state, entities };
    }
    case "life": {
      const players = patchSeat(state.players, ev.seat, { life: ev.to });
      return { ...state, players };
    }
    case "mana": {
      const players = patchSeat(state.players, ev.seat, { mana: ev.to });
      return { ...state, players };
    }
    case "max_mana": {
      const players = patchSeat(state.players, ev.seat, { max_mana: ev.to });
      return { ...state, players };
    }
    case "hand_size": {
      const players = patchSeat(state.players, ev.seat, { hand_size: ev.to });
      return { ...state, players };
    }
    case "deck_size": {
      const players = patchSeat(state.players, ev.seat, { deck_size: ev.to });
      return { ...state, players };
    }
    case "graveyard_size": {
      const players = patchSeat(state.players, ev.seat, {
        graveyard_size: ev.to,
      });
      return { ...state, players };
    }
    case "exile_size": {
      const players = patchSeat(state.players, ev.seat, { exile_size: ev.to });
      return { ...state, players };
    }
    case "phase": {
      if (COMBAT_PHASES.has(ev.to)) {
        return { ...state, phase: ev.to };
      }
      // Leaving combat. The engine clears every entity's `is_attacking`
      // / `blocking_against` at combat wrap-up but never surfaces that
      // on the wire (no Attr patch carries it), so mirror the cleanup
      // here: drop the combat snapshot and strip the per-entity combat
      // flags this reducer set on `attackers_declared` / `block`.
      // Field.tsx derives the forward lift + red/amber rings from these,
      // so this is the moment an attacker visually steps back.
      return {
        ...state,
        phase: ev.to,
        combat: null,
        entities: clearCombatFlags(state.entities),
      };
    }
    case "turn": {
      // Two pieces of EOT cleanup the engine runs but doesn't surface
      // as per-entity wire events — replicate them so the SPA's view
      // stays consistent without waiting for a snapshot resync:
      //
      // 1. `entered_this_turn` clears for EVERY creature — the engine
      //    wipes it for all of them in `phase::on_turn_end`, not just the
      //    active player's. A creature that entered under the non-active
      //    player (a trap that Troops a blocker for the defender, a
      //    control steal during the opponent's turn) entered "this turn"
      //    too and sheds sickness at this turn's end. Clearing only the
      //    active player's creatures left the opponent's freshly-summoned
      //    creature showing the summoning-sickness icon on the viewer's
      //    next turn.
      // 2. `marked_damage` clears for EVERY creature — the engine wipes
      //    the whole map in `phase::on_turn_end`. Without mirroring
      //    that here, the SPA's per-Damage `damage_marked += amount`
      //    accumulates across turns, eventually pushing
      //    `remaining_life` negative on a creature that hasn't taken
      //    enough damage in any one turn to die (the user saw this as
      //    "Khalami went to -5 life for no reason").
      const entities = new Map(state.entities);
      for (const [eid, rec] of state.entities) {
        const wasSick = !!rec.summoning_sickness;
        const hadDamage = (rec.damage_marked ?? 0) > 0;
        if (!wasSick && !hadDamage) continue;
        const cleared = {
          ...rec,
          ...(wasSick ? { summoning_sickness: false } : {}),
          ...(hadDamage
            ? {
                damage_marked: 0,
                remaining_life: rec.current_life ?? rec.remaining_life ?? 0,
              }
            : {}),
        };
        entities.set(eid, cleared);
      }
      return {
        ...state,
        turn: ev.to,
        activePlayer: ev.active,
        entities,
      };
    }
    case "die": {
      // Death is rendered via the entity moving to graveyard; the wire
      // pairs this event with the matching `Move`. Leave the entity in
      // the map so log refs still resolve; no state change here.
      return state;
    }
    case "trap_sprung": {
      // The trap's face-down → face-up + battle → graveyard transitions
      // ride on the paired Move/Attr events; this entry just lets the
      // feed render "X sprang" and lets the animation layer key off it.
      // No reducer state change.
      return state;
    }
    case "fizzled": {
      // The spell's hand → stack → graveyard transitions ride on the
      // paired Move events; this entry just lets the feed render
      // "X fizzled" so a countered cast doesn't read as a normal
      // resolution. No reducer state change.
      return state;
    }
    case "damage": {
      // The wire emits Damage events for the action ordering but does
      // NOT follow up with `Attr(damage_marked)` for creature targets;
      // for player targets the matching `Life` event lands in the
      // scalar-diff tail at the END of the batch, long after the
      // damage actually happened.
      //
      // Without applying the damage here, the chunk holding the
      // Damage event has no state diff — `hasVisibleDiff` returns
      // false, `applyEventBatch` skips the view transition AND the
      // combat lunge, and the visible "opp lost life" change only
      // shows up in the trailing scalar chunk after the opponent's
      // whole turn has animated. (Reported as: "my attack animation
      // is playing AFTER the opponent's subsequent turn.")
      //
      // Mirror the engine's `marked_damage += amount` for creatures
      // and `life -= amount` for players so the visible state moves
      // in lock-step with the lunge that this chunk fires.
      if (ev.target.kind === "entity") {
        const entities = new Map(state.entities);
        const cur = entities.get(ev.target.eid);
        if (cur) {
          const damage_marked = (cur.damage_marked ?? 0) + ev.amount;
          const base_life = cur.current_life ?? 0;
          const remaining_life = base_life - damage_marked;
          entities.set(ev.target.eid, {
            ...cur,
            damage_marked,
            remaining_life,
          });
        }
        return { ...state, entities };
      }
      const seat = ev.target.seat;
      const players = patchSeat(state.players, seat, {
        life: state.players[seat].life - ev.amount,
      });
      return { ...state, players };
    }
    case "attackers_declared": {
      const blocks = state.combat?.blocks ?? [];
      // Stamp `is_attacking` on the records too: the engine sets the
      // flag when attackers are declared but never emits an Attr patch
      // for it, and Field.tsx needs the entity-level flag to keep the
      // committed attackers lifted + red-ringed once the selection /
      // pending-driven decoration stops painting (the pending decision
      // can stay stale-`attackers` until the opponent's whole turn has
      // streamed back). Cleared when a `phase` event leaves combat.
      let entities = state.entities;
      if (ev.attackers.length > 0) {
        entities = new Map(state.entities);
        for (const eid of ev.attackers) {
          const cur = entities.get(eid);
          if (cur) entities.set(eid, { ...cur, is_attacking: true });
        }
      }
      return {
        ...state,
        entities,
        combat: { attackers: [...ev.attackers], blocks },
      };
    }
    case "block": {
      const combat = state.combat ?? { attackers: [], blocks: [] };
      const blocks = combat.blocks.slice();
      const idx = blocks.findIndex((p) => p.attacker === ev.attacker);
      if (idx >= 0) {
        const existing = blocks[idx];
        if (!existing.blockers.includes(ev.blocker)) {
          blocks[idx] = {
            attacker: ev.attacker,
            blockers: [...existing.blockers, ev.blocker],
          };
        }
      } else {
        blocks.push({ attacker: ev.attacker, blockers: [ev.blocker] });
      }
      // Mirror `blocking_against` onto the blocker's record — same
      // reasoning as `is_attacking` in `attackers_declared` above.
      let entities = state.entities;
      const blocker = state.entities.get(ev.blocker);
      if (blocker && blocker.blocking_against !== ev.attacker) {
        entities = new Map(state.entities);
        entities.set(ev.blocker, { ...blocker, blocking_against: ev.attacker });
      }
      return { ...state, entities, combat: { ...combat, blocks } };
    }
    case "castable": {
      const entities = new Map(state.entities);
      const cur = entities.get(ev.eid);
      if (cur) entities.set(ev.eid, { ...cur, castable: ev.value });
      return { ...state, entities };
    }
    case "pending_changed": {
      return { ...state, pending: ev.pending };
    }
    case "notice": {
      // Feed-only message; carries no board state to apply.
      return state;
    }
    case "game_over": {
      return { ...state, result: ev.result, terminal: true };
    }
  }
}

/** Phases during which combat decoration (`is_attacking` /
 *  `blocking_against` / `state.combat`) stays painted. Any `phase`
 *  event landing outside this set means combat is over. */
const COMBAT_PHASES: ReadonlySet<Phase> = new Set<Phase>([
  "combat_start",
  "combat_declare_attackers",
  "combat_declare_blockers",
  "combat_damage",
]);

/** Strip `is_attacking` / `blocking_against` from every record that
 *  carries either. Returns the input map untouched when nothing does. */
function clearCombatFlags(
  entities: Map<number, EntityRecord>,
): Map<number, EntityRecord> {
  let out = entities;
  for (const [eid, rec] of entities) {
    if (!rec.is_attacking && rec.blocking_against == null) continue;
    if (out === entities) out = new Map(entities);
    out.set(eid, { ...rec, is_attacking: false, blocking_against: null });
  }
  return out;
}

function applyAttrPatch(rec: EntityRecord, p: AttrPatch): EntityRecord {
  const out: EntityRecord = { ...rec };
  if (p.controller !== undefined) out.controller = p.controller;
  if (p.current_attack !== undefined) out.current_attack = p.current_attack;
  if (p.current_life !== undefined) out.current_life = p.current_life;
  if (p.damage_marked !== undefined) out.damage_marked = p.damage_marked;
  if (p.remaining_life !== undefined) out.remaining_life = p.remaining_life;
  if (p.is_attacking !== undefined) out.is_attacking = p.is_attacking;
  if ("blocking_against" in p) out.blocking_against = p.blocking_against ?? null;
  if (p.face_down !== undefined) out.face_down = p.face_down;
  if (p.summoning_sickness !== undefined) {
    out.summoning_sickness = p.summoning_sickness;
  }
  if (p.seal_counters !== undefined) out.seal_counters = p.seal_counters;
  // When a stats patch updates current_life but not remaining_life
  // (the v2 EntityStatsChanged → Attr path leaves damage_marked /
  // remaining_life to the Damage event channel), recompute remaining
  // life from the new base + the SPA's existing damage_marked.
  // Without this, a buff that bumps a creature's life past her old
  // bar leaves remaining_life stale.
  if (
    p.current_life !== undefined &&
    p.remaining_life === undefined
  ) {
    out.remaining_life = (out.current_life ?? 0) - (out.damage_marked ?? 0);
  }
  return out;
}

function patchSeat(
  players: [PlayerScalars, PlayerScalars],
  seat: PlayerId,
  patch: Partial<PlayerScalars>,
): [PlayerScalars, PlayerScalars] {
  const next: [PlayerScalars, PlayerScalars] = [players[0], players[1]];
  next[seat] = { ...next[seat], ...patch };
  return next;
}

function removeFromZone(
  arr: number[],
  evFrom: string | null | undefined,
  zoneName: string,
  eid: number,
): number[] {
  if (evFrom !== zoneName) return arr;
  return arr.filter((e) => e !== eid);
}

function appendToZone(
  arr: number[],
  evTo: string,
  zoneName: string,
  eid: number,
): number[] {
  if (evTo !== zoneName) return arr;
  if (arr.includes(eid)) return arr;
  return [...arr, eid];
}

function appendRevealToZone(
  scalars: PlayerScalars,
  zone: string,
  eid: number,
): PlayerScalars {
  const fieldFor = (z: string): keyof PlayerScalars | null => {
    switch (z) {
      case "hand":
        return "hand";
      case "deck":
        return "deck";
      case "battle":
        return "battle";
      case "graveyard":
        return "graveyard";
      case "exile":
        return "exile";
      default:
        return null;
    }
  };
  const field = fieldFor(zone);
  if (field == null) return scalars;
  const cur = scalars[field] as number[];
  if (cur.includes(eid)) return scalars;
  return { ...scalars, [field]: [...cur, eid] };
}
